import React, { useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useWikiV2Client } from '../../../hooks/useWikiV2';
import { usePlan } from '../../../context/PlanContext';
import { useWikiCategories } from '../shared/useWikiQueries';
import { wikiPaths } from '../shared/wikiRoutes';
import { wikiUi } from '../shared/wikiUi';
import WikiModalShell, {
    WikiCreateButton,
    WikiField,
    WikiModalError,
    WikiSegmented,
    WikiToggleCard,
} from './WikiModalShell';
import WikiImportPanel from './WikiImportPanel';
import { WikiLocationLayer, WikiLocationTrigger, useWikiLocationPicker } from './WikiLocationPicker';
import useWikiImportFile from './useWikiImportFile';
import { buildWikiLocations, resolveDefaultLocation } from './wikiLocations';
import {
    WIKI_MODAL_COPY,
    WIKI_MODAL_TOASTS,
    WIKI_PAGE_MODES,
    WIKI_TEMPLATE_NAMES,
    normalizeTemplateName,
    templateMarkdown,
} from './wikiModalCopy';
import { wikiModalErrorText, wikiPlanRefusal } from './wikiModalErrors';
import { primeCreatedPage } from './wikiModalCache';

/**
 * Router state handed to `/wiki/p/:id/edit` so the editor knows how it was
 * opened (prototype `wkStartEdit(id, md, title, isNew)` → `focusOnLoad`):
 * a new page puts the caret in the first empty paragraph; an imported draft
 * opens at the first block without stealing focus.
 */
export const WIKI_EDITOR_STATE_KEY = 'wikiEditor';

/**
 * Plan features the modal hides (PLAN-CP contract §1): `wiki_import` → the
 * "Import a file" mode, `wiki_acks` → "Require acknowledgement". While the
 * plan is unknown both stay visible and the server refuses if it must.
 */
export function wikiPageFeatures(plan) {
    const known = Boolean(plan) && !plan.planUnknown && typeof plan.checkFeature === 'function';
    return {
        importAllowed: !known || plan.checkFeature('wiki_import'),
        acksAllowed: !known || plan.checkFeature('wiki_acks'),
    };
}

/** Prototype opener state (`wkOpenNewPage` / `wkOpenImport`, C2 §1.1). */
export function initialPageForm(opts = {}) {
    const mode = opts.mode === 'import' ? 'import' : 'write';
    return {
        mode,
        name: '',
        loc: null, // null = the default (opener's location, else the first)
        tpl: normalizeTemplateName(opts.template) || (mode === 'import' ? 'Blank' : 'How-to'),
        required: false,
        importTitleEdited: false,
    };
}

/**
 * New page / Import a page (C2 §1.5). One modal, two modes that share NAME,
 * LOCATION, template and "Require acknowledgement" (switching keeps them and
 * the loaded file).
 *
 * Write → "Create and start writing": POST /wiki/pages as a DRAFT with the
 * template's Markdown, then straight into the inline editor
 * (`/wiki/p/:id/edit`, caret in the first empty paragraph). No toast.
 * Import → "Publish now" (page view + toast) or "Create draft and review"
 * (inline editor + toast) via POST /wiki/import/commit, subcategory included.
 *
 * Plan gates: no `wiki_import` → no mode switch and Write only (an Import
 * opener lands in Write); no `wiki_acks` → no "Require acknowledgement" (and
 * `required_ack:false`). A plan refusal from the server (403
 * `wiki_limit_reached` / `wiki_feature_disabled`) closes the modal and toasts
 * its message, so the app's upgrade modal is on top; other errors stay inline.
 */
export default function WikiPageModal({ opts = {}, routeLocationId = null, onClose }) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const client = useWikiV2Client();
    const categories = useWikiCategories();
    const plan = usePlan();
    const importer = useWikiImportFile({
        onPlanRefusal: (refusal) => {
            onClose();
            wikiUi.toast(refusal.message);
        },
    });
    const [form, setForm] = useState(() => initialPageForm(opts));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const nameId = useId();
    const listboxId = useId();

    const locations = useMemo(() => buildWikiLocations(categories.tree), [categories.tree]);
    const defaultId = resolveDefaultLocation(locations, [opts.subcategoryId, opts.categoryId, routeLocationId]);
    const currentId = form.loc && locations.some((loc) => loc.id === form.loc) ? form.loc : defaultId;
    const current = locations.find((loc) => loc.id === currentId) || null;

    const picker = useWikiLocationPicker({
        locations,
        currentId,
        onPick: (id) => setForm((prev) => ({ ...prev, loc: id })),
    });

    const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
    const { importAllowed, acksAllowed } = wikiPageFeatures(plan);
    const isImport = importAllowed && form.mode === 'import';
    const requiredAck = acksAllowed && Boolean(form.required);
    const { imp } = importer;
    const parsed = Boolean(imp && !imp.error && Array.isArray(imp.blocks));

    const canWrite = Boolean(form.name.trim()) && Boolean(current);
    const canImport = parsed && Boolean(current);

    const fail = (err) => {
        const refusal = wikiPlanRefusal(err);
        if (refusal) {
            onClose();
            wikiUi.toast(refusal.message);
            return;
        }
        setBusy(false);
        setError(wikiModalErrorText(err));
    };

    const createWrite = async () => {
        if (!canWrite || busy) return;
        setBusy(true);
        setError('');
        try {
            const page = await client.post('/wiki/pages', {
                category_id: current.categoryId,
                subcategory_id: current.subcategoryId,
                title: form.name.trim(),
                body_md: templateMarkdown(form.tpl),
                required_ack: requiredAck,
                status: 'draft',
            });
            if (!page || !page.page_id) throw new Error('The new page has no id.');
            primeCreatedPage(queryClient, page, { requiredAck });
            onClose();
            navigate(wikiPaths.pageEdit(page.page_id), {
                state: { [WIKI_EDITOR_STATE_KEY]: { isNew: true, focusOnLoad: true, template: form.tpl } },
            });
        } catch (err) {
            fail(err);
        }
    };

    const commitImport = (publish) => async () => {
        if (!canImport || busy) return;
        setBusy(true);
        setError('');
        const blocks = imp.blocks.length;
        let token;
        try {
            token = await importer.awaitToken();
        } catch {
            // The upload failed: its message is already in the import error box.
            setBusy(false);
            return;
        }
        try {
            const title = String(form.name || imp.title || 'Imported page').trim();
            const page = await client.post('/wiki/import/commit', {
                preview_token: token,
                category_id: current.categoryId,
                subcategory_id: current.subcategoryId,
                title,
                publish,
                required_ack: requiredAck,
            });
            if (!page || !page.page_id) throw new Error('The imported page has no id.');
            primeCreatedPage(queryClient, page, { requiredAck });
            onClose();
            if (publish) {
                navigate(wikiPaths.page(page.page_id));
                wikiUi.toast(WIKI_MODAL_TOASTS.importPublished(blocks));
            } else {
                navigate(wikiPaths.pageEdit(page.page_id), {
                    state: { [WIKI_EDITOR_STATE_KEY]: { isNew: false, focusOnLoad: false, imported: true } },
                });
                wikiUi.toast(WIKI_MODAL_TOASTS.importDraft(blocks));
            }
        } catch (err) {
            fail(err);
        }
    };

    const onNameKeyDown = (event) => {
        if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
            event.preventDefault();
            createWrite();
        }
    };

    // Prototype `wkImpTitle`: the typed name, else the detected title. Once
    // the user edits PAGE TITLE it may be empty (detected title = placeholder).
    const importTitleValue = form.importTitleEdited ? form.name : (form.name || (imp && imp.title) || '');

    const footer = isImport ? (
        <>
            <WikiCreateButton
                variant="secondary"
                enabled={canImport}
                busy={busy}
                onClick={commitImport(true)}
                action="wiki.import_publish"
                testId="wiki-import-publish"
            >
                {WIKI_MODAL_COPY.publishNow}
            </WikiCreateButton>
            <WikiCreateButton
                enabled={canImport}
                busy={busy}
                onClick={commitImport(false)}
                action="wiki.import_draft"
                testId="wiki-import-draft"
            >
                {WIKI_MODAL_COPY.createDraft}
            </WikiCreateButton>
        </>
    ) : (
        <WikiCreateButton
            enabled={canWrite}
            busy={busy}
            onClick={createWrite}
            icon="pencil"
            iconSize={14}
            action="wiki.create_page"
            testId="wiki-modal-create"
        >
            {WIKI_MODAL_COPY.createAndWrite}
        </WikiCreateButton>
    );

    return (
        <WikiModalShell
            title={isImport ? WIKI_MODAL_COPY.titleImport : WIKI_MODAL_COPY.titlePage}
            onClose={onClose}
            testId="wiki-page-modal"
            beforeCard={<WikiLocationLayer picker={picker} listboxId={listboxId} />}
            footer={footer}
        >
            {importAllowed && (
                <WikiSegmented
                    block
                    label="Page source"
                    options={WIKI_PAGE_MODES}
                    value={isImport ? 'import' : 'write'}
                    onChange={(mode) => { setError(''); set('mode')(mode); }}
                    testId="wiki-page-mode"
                />
            )}

            {isImport ? (
                <WikiImportPanel
                    key="import"
                    importer={importer}
                    titleValue={importTitleValue}
                    titlePlaceholder={(imp && imp.title) || ''}
                    onTitleChange={(value) => setForm((prev) => ({ ...prev, name: value, importTitleEdited: true }))}
                />
            ) : (
                <WikiField label={WIKI_MODAL_COPY.nameLabel} required htmlFor={nameId}>
                    <input
                        id={nameId}
                        className="wk-modal-input"
                        value={form.name}
                        onChange={(event) => set('name')(event.target.value)}
                        onKeyDown={onNameKeyDown}
                        placeholder={WIKI_MODAL_COPY.namePlaceholder}
                        maxLength={240}
                        required
                        aria-required="true"
                        data-wk-autofocus
                        data-field="wiki.page_title"
                        data-testid="wiki-modal-name"
                    />
                </WikiField>
            )}

            <WikiField label={WIKI_MODAL_COPY.locationLabel}>
                <WikiLocationTrigger picker={picker} label={current ? current.label : ''} listboxId={listboxId} />
            </WikiField>

            {!isImport && (
                <WikiField label={WIKI_MODAL_COPY.startFromLabel} variant="group">
                    <WikiSegmented
                        label={WIKI_MODAL_COPY.startFromLabel}
                        options={WIKI_TEMPLATE_NAMES}
                        value={form.tpl}
                        onChange={set('tpl')}
                        testId="wiki-page-template"
                    />
                </WikiField>
            )}

            {acksAllowed && (
                <WikiToggleCard
                    title={WIKI_MODAL_COPY.requiredTitle}
                    description={WIKI_MODAL_COPY.requiredDesc}
                    checked={form.required}
                    onChange={set('required')}
                    testId="wiki-page-required"
                />
            )}

            <WikiModalError message={error} />
        </WikiModalShell>
    );
}
